import { base44 } from '@/api/base44Client';

/**
 * ============================================================================
 * SATALA NOTIFICATION ENGINE (SISTEM NOTIFIKASI PINTAR SATALA)
 * ============================================================================
 * Menangani distribusi notifikasi cerdas dengan fitur:
 * 1. Actor Exemption: Pembuat aksi (creator) otomatis tidak dikirimi notifikasi ke perangkatnya sendiri.
 * 2. Smart Routing: Membedakan pesan untuk PIC penanggung jawab vs pesan broadcast monitoring (Finance/Owner).
 * 3. User Preferences: Mematuhi preferensi notifikasi per pengguna (Hanya PIC, Semua, atau OFF).
 * 4. Multi-Channel: Menyimpan ke In-App Notification dan mengirim Web Push Notification jika diizinkan.
 */

// Event Definitions & Default Config (Fokus Step-by-Step: Modul SPH & Modul Invoice & PO)
export const NOTIFICATION_EVENTS = {
  // 1. Modul SPH (Surat Penawaran Harga)
  SPH_CREATED: {
    id: 'sph_created',
    name: 'SPH Baru Diterbitkan',
    category: 'FINANCE',
    relatedPortals: ['FINANCE', 'MARKETING'],
    moduleName: 'Daftar SPH',
    categoryColor: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    description: 'Surat Penawaran Harga (SPH) baru telah dibuat di sistem',
    defaultRoles: ['finance', 'marketing', 'owner', 'developer'],
    allowPicMode: true,
    icon: 'FileText',
  },
  SPH_UPDATED: {
    id: 'sph_updated',
    name: 'SPH Diedit & Disimpan',
    category: 'FINANCE',
    relatedPortals: ['FINANCE', 'MARKETING'],
    moduleName: 'Daftar SPH',
    categoryColor: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    description: 'Dokumen penawaran harga SPH telah diperbarui dan disimpan',
    defaultRoles: ['finance', 'marketing', 'owner', 'developer'],
    allowPicMode: true,
    icon: 'FileText',
  },
  SPH_APPROVED: {
    id: 'sph_approved',
    name: 'SPH Disetujui / ACC Klien',
    category: 'FINANCE',
    relatedPortals: ['FINANCE', 'MARKETING'],
    moduleName: 'Daftar SPH',
    categoryColor: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    description: 'Penawaran harga SPH telah disetujui / di-ACC klien',
    defaultRoles: ['finance', 'marketing', 'owner', 'developer'],
    allowPicMode: true,
    icon: 'CheckCircle2',
  },

  // 2. Modul Invoice & PO
  INVOICE_CREATED: {
    id: 'invoice_created',
    name: 'Invoice Baru Dibuat',
    category: 'FINANCE',
    relatedPortals: ['FINANCE', 'MARKETING'],
    moduleName: 'Invoice & PO',
    categoryColor: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    description: 'Invoice tagihan baru telah dibuat di sistem',
    defaultRoles: ['finance', 'owner', 'developer', 'admin', 'marketing'],
    allowPicMode: true,
    icon: 'FileText',
  },
  INVOICE_UPDATED: {
    id: 'invoice_updated',
    name: 'Invoice Diedit & Disimpan',
    category: 'FINANCE',
    relatedPortals: ['FINANCE', 'MARKETING'],
    moduleName: 'Invoice & PO',
    categoryColor: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    description: 'Data invoice tagihan telah diperbarui dan disimpan',
    defaultRoles: ['finance', 'owner', 'developer', 'marketing'],
    allowPicMode: true,
    icon: 'FileText',
  },
  INVOICE_PAYMENT_VERIFIED: {
    id: 'invoice_payment_verified',
    name: 'Pembayaran Masuk / Diverifikasi',
    category: 'FINANCE',
    relatedPortals: ['FINANCE', 'MARKETING'],
    moduleName: 'Invoice & PO',
    categoryColor: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    description: 'Pembayaran DP / Pelunasan invoice telah diverifikasi',
    defaultRoles: ['finance', 'owner', 'developer', 'marketing'],
    allowPicMode: true,
    icon: 'Receipt',
  },
  INVOICE_TO_PO: {
    id: 'invoice_to_po',
    name: 'Invoice Naik ke PO Produksi',
    category: 'PRODUKSI',
    relatedPortals: ['FINANCE', 'PRODUKSI', 'MARKETING'],
    moduleName: 'Invoice & PO',
    categoryColor: 'bg-blue-50 text-blue-800 border-blue-200',
    description: 'Invoice telah dinaikkan statusnya menjadi PO Produksi (antrean pabrik)',
    defaultRoles: ['produksi', 'finance', 'owner', 'developer', 'marketing'],
    allowPicMode: true,
    icon: 'UploadCloud',
  },

  // 3. Modul Invoice Custom & Sample
  CUSTOM_INVOICE_CREATED: {
    id: 'custom_invoice_created',
    name: 'Invoice Sample & Custom Dibuat',
    category: 'FINANCE',
    relatedPortals: ['FINANCE', 'MARKETING'],
    moduleName: 'Invoice Custom & Sample',
    categoryColor: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    description: 'Tagihan biaya sample / custom maklon baru telah dibuat di sistem',
    defaultRoles: ['finance', 'marketing', 'owner', 'developer'],
    allowPicMode: true,
    icon: 'FileText',
  },
  CUSTOM_INVOICE_UPDATED: {
    id: 'custom_invoice_updated',
    name: 'Invoice Sample & Custom Diedit',
    category: 'FINANCE',
    relatedPortals: ['FINANCE', 'MARKETING'],
    moduleName: 'Invoice Custom & Sample',
    categoryColor: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    description: 'Data tagihan invoice sample / custom telah diperbarui dan disimpan',
    defaultRoles: ['finance', 'marketing', 'owner', 'developer'],
    allowPicMode: true,
    icon: 'FileText',
  },
  CUSTOM_INVOICE_PAID: {
    id: 'custom_invoice_paid',
    name: 'Pembayaran Sample Diverifikasi',
    category: 'FINANCE',
    relatedPortals: ['FINANCE', 'MARKETING', 'PRODUKSI'],
    moduleName: 'Invoice Custom & Sample',
    categoryColor: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    description: 'Biaya sample / custom telah lunas dan siap untuk diracik formulanya',
    defaultRoles: ['finance', 'marketing', 'produksi', 'owner', 'developer'],
    allowPicMode: true,
    icon: 'Receipt',
  },
  CUSTOM_INVOICE_TO_SAMPLE_PO: {
    id: 'custom_invoice_to_sample_po',
    name: 'Sample Naik ke PO Sample (SPL)',
    category: 'PRODUKSI',
    relatedPortals: ['FINANCE', 'PRODUKSI', 'MARKETING'],
    moduleName: 'Invoice Custom & Sample',
    categoryColor: 'bg-blue-50 text-blue-800 border-blue-200',
    description: 'Pesanan sample resmi masuk antrean pengerjaan formulasi (SPL)',
    defaultRoles: ['produksi', 'finance', 'marketing', 'owner', 'developer'],
    allowPicMode: true,
    icon: 'UploadCloud',
  },

  // 4. Modul Hutang Perusahaan
  PAYABLE_DUE_SOON: {
    id: 'payable_due_soon',
    name: 'Hutang Vendor Jatuh Tempo (H-3)',
    category: 'FINANCE',
    relatedPortals: ['FINANCE'],
    moduleName: 'Hutang Perusahaan',
    categoryColor: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    description: 'Tagihan vendor/supplier bahan/kemasan akan jatuh tempo dalam 3 hari',
    defaultRoles: ['finance', 'owner', 'developer'],
    allowPicMode: false,
    icon: 'AlertTriangle',
  },
  PAYABLE_DUE_TODAY: {
    id: 'payable_due_today',
    name: 'Hutang Vendor Jatuh Tempo Hari Ini',
    category: 'FINANCE',
    relatedPortals: ['FINANCE'],
    moduleName: 'Hutang Perusahaan',
    categoryColor: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    description: 'Tagihan vendor/supplier bahan/kemasan jatuh tempo HARI INI dan harus dilunasi',
    defaultRoles: ['finance', 'owner', 'developer'],
    allowPicMode: false,
    icon: 'AlertTriangle',
  },

  // 5. Modul Piutang Klien
  RECEIVABLE_DUE_TODAY: {
    id: 'receivable_due_today',
    name: 'Piutang Klien Jatuh Tempo Hari Ini',
    category: 'FINANCE',
    relatedPortals: ['FINANCE', 'MARKETING'],
    moduleName: 'Piutang Klien',
    categoryColor: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    description: 'Sisa tagihan invoice klien jatuh tempo HARI INI dan perlu segera ditagih',
    defaultRoles: ['finance', 'marketing', 'owner', 'developer'],
    allowPicMode: true,
    icon: 'AlertTriangle',
  },

  // 6. Modul Pengajuan Pembelian
  PURCHASING_REQUEST_CREATED: {
    id: 'purchasing_request_created',
    name: 'Request Dana Pembelian Masuk',
    category: 'FINANCE',
    relatedPortals: ['FINANCE', 'PURCHASING'],
    moduleName: 'Pengajuan Pembelian',
    categoryColor: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    description: 'Permohonan dana pembelian bahan/kemasan baru diajukan oleh Purchasing',
    defaultRoles: ['finance', 'purchasing', 'owner', 'developer'],
    allowPicMode: true,
    icon: 'ShoppingBag',
  },
  PURCHASING_REQUEST_DISBURSED: {
    id: 'purchasing_request_disbursed',
    name: 'Dana Pembelian Telah Ditransfer',
    category: 'FINANCE',
    relatedPortals: ['FINANCE', 'PURCHASING'],
    moduleName: 'Pengajuan Pembelian',
    categoryColor: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    description: 'Dana pembelian telah disetujui & ditransfer oleh Finance',
    defaultRoles: ['purchasing', 'finance', 'owner', 'developer'],
    allowPicMode: true,
    icon: 'Receipt',
  },
  PURCHASING_REQUEST_TEMPO_APPROVED: {
    id: 'purchasing_request_tempo_approved',
    name: 'Persetujuan Pembelian Tempo Disetujui',
    category: 'FINANCE',
    relatedPortals: ['FINANCE', 'PURCHASING'],
    moduleName: 'Pengajuan Pembelian',
    categoryColor: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    description: 'Pengajuan pembelian sistem tempo/termin telah disetujui Finance',
    defaultRoles: ['purchasing', 'finance', 'owner', 'developer'],
    allowPicMode: true,
    icon: 'CheckCircle2',
  },

  // 7. Modul Laporan Reject
  REJECT_COST_REPORTED: {
    id: 'reject_cost_reported',
    name: 'Kerugian Reject Pabrik Dilaporkan',
    category: 'FINANCE',
    relatedPortals: ['FINANCE', 'PRODUKSI'],
    moduleName: 'Laporan Reject',
    categoryColor: 'bg-rose-50 text-rose-800 border-rose-200',
    description: 'Laporan kerugian finansial/material akibat batch reject pabrik',
    defaultRoles: ['finance', 'produksi', 'owner', 'developer'],
    allowPicMode: true,
    icon: 'AlertCircle',
  },

  // 8. Modul Sample Racikan (Portal Marketing & Lab Formulasi)
  SAMPLE_ASSIGNED_TO_FORMULATOR: {
    id: 'sample_assigned_to_formulator',
    name: 'Tugas Racikan Sample Baru',
    category: 'MARKETING',
    relatedPortals: ['MARKETING', 'PRODUKSI'],
    moduleName: 'Sample Racikan',
    categoryColor: 'bg-amber-50 text-amber-800 border-amber-200',
    description: 'Tugas baru meracik formula tester sample ditugaskan ke PIC Pembuat Sample',
    defaultRoles: ['produksi', 'marketing', 'owner', 'developer'],
    allowPicMode: true,
    icon: 'FlaskConical',
  },
  SAMPLE_IN_PROGRESS: {
    id: 'sample_in_progress',
    name: 'Sample Sedang Dikerjakan di Lab',
    category: 'MARKETING',
    relatedPortals: ['MARKETING', 'PRODUKSI'],
    moduleName: 'Sample Racikan',
    categoryColor: 'bg-amber-50 text-amber-800 border-amber-200',
    description: 'Formulasi sample telah diisi dan sedang dalam proses peracikan oleh formulator',
    defaultRoles: ['marketing', 'produksi', 'owner', 'developer'],
    allowPicMode: true,
    icon: 'Clock',
  },
  SAMPLE_SHIPPED_TO_CLIENT: {
    id: 'sample_shipped_to_client',
    name: 'Sample Telah Dikirim ke Klien',
    category: 'MARKETING',
    relatedPortals: ['MARKETING', 'PRODUKSI'],
    moduleName: 'Sample Racikan',
    categoryColor: 'bg-amber-50 text-amber-800 border-amber-200',
    description: 'Sample tester telah selesai diracik dan statusnya diubah ke Pengiriman',
    defaultRoles: ['marketing', 'owner', 'developer'],
    allowPicMode: true,
    icon: 'Truck',
  },
  SAMPLE_FOLLOWUP_DAY5: {
    id: 'sample_followup_day5',
    name: 'Pengingat Follow-up Klien (H+5 Pengiriman)',
    category: 'MARKETING',
    relatedPortals: ['MARKETING'],
    moduleName: 'Sample Racikan',
    categoryColor: 'bg-amber-50 text-amber-800 border-amber-200',
    description: 'Sudah 5 hari sejak pengiriman sample dan belum ada respon feedback dari klien',
    defaultRoles: ['marketing', 'owner', 'developer'],
    allowPicMode: true,
    icon: 'PhoneCall',
  },
  SAMPLE_FOLLOWUP_DAY10: {
    id: 'sample_followup_day10',
    name: 'Pengingat Follow-up Kedua (H+10 Belum Respon)',
    category: 'MARKETING',
    relatedPortals: ['MARKETING'],
    moduleName: 'Sample Racikan',
    categoryColor: 'bg-rose-50 text-rose-800 border-rose-200',
    description: 'Sudah 10 hari sejak pengiriman sample dan status sample masih belum di-ACC / revisi',
    defaultRoles: ['marketing', 'owner', 'developer'],
    allowPicMode: true,
    icon: 'AlertTriangle',
  },
  SAMPLE_VARIANT_DECIDED: {
    id: 'sample_variant_decided',
    name: 'Hasil Review Sample (ACC / Revisi Klien)',
    category: 'MARKETING',
    relatedPortals: ['MARKETING', 'PRODUKSI'],
    moduleName: 'Sample Racikan',
    categoryColor: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    description: 'Klien telah memberikan keputusan ACC atau catatan revisi untuk varian sample',
    defaultRoles: ['produksi', 'marketing', 'owner', 'developer'],
    allowPicMode: true,
    icon: 'CheckCircle2',
  },

  // 9. Modul Progress Produksi PO Pabrik (Portal Produksi & Marketing)
  PRODUCTION_MIXING_COMPLETED: {
    id: 'production_mixing_completed',
    name: 'Mixing Selesai Seluruh Varian',
    category: 'PRODUKSI',
    relatedPortals: ['PRODUKSI', 'MARKETING'],
    moduleName: 'Progress Produksi PO',
    categoryColor: 'bg-blue-50 text-blue-800 border-blue-200',
    description: 'Proses mixing telah selesai dikerjakan untuk seluruh varian dalam 1 PO',
    defaultRoles: ['marketing', 'produksi', 'owner', 'developer'],
    allowPicMode: true,
    icon: 'CheckCircle',
  },
  PRODUCTION_FILLING_COMPLETED: {
    id: 'production_filling_completed',
    name: 'Filling Selesai Seluruh Varian',
    category: 'PRODUKSI',
    relatedPortals: ['PRODUKSI', 'MARKETING'],
    moduleName: 'Progress Produksi PO',
    categoryColor: 'bg-blue-50 text-blue-800 border-blue-200',
    description: 'Proses filling ke botol/kemasan primer telah selesai untuk seluruh varian',
    defaultRoles: ['marketing', 'produksi', 'owner', 'developer'],
    allowPicMode: true,
    icon: 'CheckCircle',
  },
  PRODUCTION_PACKING_COMPLETED: {
    id: 'production_packing_completed',
    name: 'Pengemasan Selesai Seluruh Varian',
    category: 'PRODUKSI',
    relatedPortals: ['PRODUKSI', 'MARKETING'],
    moduleName: 'Progress Produksi PO',
    categoryColor: 'bg-blue-50 text-blue-800 border-blue-200',
    description: 'Proses pengemasan sekunder (box/segel/packing) telah tuntas 100%',
    defaultRoles: ['marketing', 'produksi', 'owner', 'developer'],
    allowPicMode: true,
    icon: 'CheckCircle',
  },
  PRODUCTION_READY_TO_SHIP: {
    id: 'production_ready_to_ship',
    name: 'PO Selesai & Surat Jalan Terbit (Ready to Ship)',
    category: 'PRODUKSI',
    relatedPortals: ['PRODUKSI', 'MARKETING', 'FINANCE'],
    moduleName: 'Progress Produksi PO',
    categoryColor: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    description: 'Pesanan PO telah selesai diproduksi dan Surat Jalan siap dikirim ke klien',
    defaultRoles: ['marketing', 'finance', 'produksi', 'owner', 'developer'],
    allowPicMode: true,
    icon: 'Send',
  },

  // 10. Modul Kebutuhan Barang Beli (Portal Purchasing)
  PO_ITEMS_TO_BUY_NEEDED: {
    id: 'po_items_to_buy_needed',
    name: 'Kebutuhan Bahan untuk PO Baru',
    category: 'PURCHASING',
    relatedPortals: ['PURCHASING'],
    moduleName: 'Kebutuhan Barang Beli',
    categoryColor: 'bg-amber-50 text-amber-800 border-amber-200',
    description: 'Ada kekurangan bahan baku/kemasan yang harus dibeli untuk memproses PO pesanan baru',
    defaultRoles: ['purchasing', 'owner', 'developer'],
    allowPicMode: false,
    icon: 'ShoppingCart',
  },
  STOCK_LOW_ALERT: {
    id: 'stock_low_alert',
    name: 'Peringatan Stok Gudang Menipis',
    category: 'PURCHASING',
    relatedPortals: ['PURCHASING'],
    moduleName: 'Kebutuhan Barang Beli',
    categoryColor: 'bg-rose-50 text-rose-800 border-rose-200',
    description: 'Stok bahan baku/kemasan di gudang berada di bawah batas minimum (safety stock)',
    defaultRoles: ['purchasing', 'owner', 'developer'],
    allowPicMode: false,
    icon: 'AlertTriangle',
  },

  // 11. Modul Tugas Saya & Antrean Pabrik (Internal Produksi)
  TASK_MIXING_ACTIVE: {
    id: 'task_mixing_active',
    name: 'Tugas Aktif Mixing Masuk',
    category: 'PRODUKSI',
    relatedPortals: ['PRODUKSI'],
    moduleName: 'Tugas Saya Pabrik',
    categoryColor: 'bg-blue-50 text-blue-800 border-blue-200',
    description: 'Tugas baru resmi masuk ke antrean Tugas Aktif Mixing formula',
    defaultRoles: ['produksi', 'owner', 'developer'],
    allowPicMode: false,
    icon: 'PlayCircle',
  },
  TASK_QC_VALIDATION_NEEDED: {
    id: 'task_qc_validation_needed',
    name: 'Audit Validasi QC Diperlukan',
    category: 'PRODUKSI',
    relatedPortals: ['PRODUKSI'],
    moduleName: 'Tugas Saya Pabrik',
    categoryColor: 'bg-amber-50 text-amber-800 border-amber-200',
    description: 'Hasil mixing formula telah selesai & menunggu validasi/audit kelayakan oleh QC',
    defaultRoles: ['produksi', 'owner', 'developer'],
    allowPicMode: false,
    icon: 'CheckSquare',
  },
  TASK_FILLING_ACTIVE: {
    id: 'task_filling_active',
    name: 'Tugas Aktif Filling (Primer) Masuk',
    category: 'PRODUKSI',
    relatedPortals: ['PRODUKSI'],
    moduleName: 'Tugas Saya Pabrik',
    categoryColor: 'bg-blue-50 text-blue-800 border-blue-200',
    description: 'Formula selesai divalidasi QC & masuk ke Tugas Aktif Tim Primer / Filling',
    defaultRoles: ['produksi', 'owner', 'developer'],
    allowPicMode: false,
    icon: 'PlayCircle',
  },
  TASK_PACKING_ACTIVE: {
    id: 'task_packing_active',
    name: 'Tugas Aktif Pengemasan (Sekunder) Masuk',
    category: 'PRODUKSI',
    relatedPortals: ['PRODUKSI'],
    moduleName: 'Tugas Saya Pabrik',
    categoryColor: 'bg-blue-50 text-blue-800 border-blue-200',
    description: 'Produk siap masuk ke Tugas Aktif Tim Sekunder untuk box, stiker, dan segel',
    defaultRoles: ['produksi', 'owner', 'developer'],
    allowPicMode: false,
    icon: 'PlayCircle',
  },

  // 12. Modul Absensi & Kehadiran Karyawan (Portal Workspace)
  ATTENDANCE_REMINDER_MORNING: {
    id: 'attendance_reminder_morning',
    name: 'Pengingat Absen Masuk (07:30 WIB)',
    category: 'WORKSPACE',
    relatedPortals: ['WORKSPACE'],
    moduleName: 'Absensi & Kehadiran',
    categoryColor: 'bg-indigo-50 text-indigo-800 border-indigo-200',
    description: 'Pengingat lembut jam 07:30 pagi bagi seluruh karyawan yang belum check-in',
    defaultRoles: ['marketing', 'finance', 'purchasing', 'produksi', 'owner', 'developer'],
    allowPicMode: false,
    icon: 'Clock',
  },
  ATTENDANCE_WARNING_URGENT: {
    id: 'attendance_warning_urgent',
    name: 'Peringatan Mendesak Telat Masuk (08:10 WIB)',
    category: 'WORKSPACE',
    relatedPortals: ['WORKSPACE'],
    moduleName: 'Absensi & Kehadiran',
    categoryColor: 'bg-rose-50 text-rose-800 border-rose-200',
    description: 'Peringatan mendesak jam 08:10 pagi bagi karyawan yang belum absen agar segera check-in',
    defaultRoles: ['marketing', 'finance', 'purchasing', 'produksi', 'owner', 'developer'],
    allowPicMode: false,
    icon: 'AlertCircle',
  },
  ATTENDANCE_AUDIT_MIDDAY: {
    id: 'attendance_audit_midday',
    name: 'Laporan Audit Belum Absen & 1/2 Hari (12:00 WIB)',
    category: 'WORKSPACE',
    relatedPortals: ['WORKSPACE'],
    moduleName: 'Absensi & Kehadiran',
    categoryColor: 'bg-amber-50 text-amber-800 border-amber-200',
    description: 'Rekapitulasi jam 12:00 siang ke HRD/Owner mengenai karyawan yang belum absen / setengah hari',
    defaultRoles: ['owner', 'developer'],
    allowPicMode: false,
    icon: 'UserX',
  },
  LEAVE_REQUEST_SUBMITTED: {
    id: 'leave_request_submitted',
    name: 'Pengajuan Izin / Cuti Karyawan Baru',
    category: 'WORKSPACE',
    relatedPortals: ['WORKSPACE'],
    moduleName: 'Absensi & Kehadiran',
    categoryColor: 'bg-purple-50 text-purple-800 border-purple-200',
    description: 'Permohonan izin sakit, cuti, atau keperluan mendesak dari karyawan untuk di-ACC',
    defaultRoles: ['owner', 'developer'],
    allowPicMode: false,
    icon: 'FileText',
  },
  LEAVE_REQUEST_DECIDED: {
    id: 'leave_request_decided',
    name: 'Status Pengajuan Izin Disetujui / Ditolak',
    category: 'WORKSPACE',
    relatedPortals: ['WORKSPACE'],
    moduleName: 'Absensi & Kehadiran',
    categoryColor: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    description: 'Pemberitahuan kepada karyawan pemohon bahwa pengajuan izinnya telah disetujui/ditolak',
    defaultRoles: ['marketing', 'finance', 'purchasing', 'produksi', 'owner', 'developer'],
    allowPicMode: true,
    icon: 'CheckSquare',
  },

  // 13. Modul Manajemen Tim & Sistem (Portal Workspace)
  TEAM_MEMBER_ADDED: {
    id: 'team_member_added',
    name: 'Anggota Tim Baru Ditambahkan',
    category: 'WORKSPACE',
    relatedPortals: ['WORKSPACE'],
    moduleName: 'Manajemen Tim & Sistem',
    categoryColor: 'bg-indigo-50 text-indigo-800 border-indigo-200',
    description: 'Akun staf / operator / karyawan baru resmi didaftarkan di sistem Satala',
    defaultRoles: ['owner', 'developer'],
    allowPicMode: false,
    icon: 'UserPlus',
  },
  BIRTHDAY_GREETING_TODAY: {
    id: 'birthday_greeting_today',
    name: 'Ulang Tahun Karyawan Hari Ini',
    category: 'WORKSPACE',
    relatedPortals: ['WORKSPACE'],
    moduleName: 'Manajemen Tim & Sistem',
    categoryColor: 'bg-pink-50 text-pink-800 border-pink-200',
    description: 'Ucapan selamat ulang tahun hangat dari ASA dan pengingat hari bahagia ke seluruh rekan tim',
    defaultRoles: ['marketing', 'finance', 'purchasing', 'produksi', 'owner', 'developer'],
    allowPicMode: false,
    icon: 'Gift',
  },
  BACKUP_DATABASE_REMINDER: {
    id: 'backup_database_reminder',
    name: 'Pengingat Backup Database Rutin',
    category: 'WORKSPACE',
    relatedPortals: ['WORKSPACE'],
    moduleName: 'Manajemen Tim & Sistem',
    categoryColor: 'bg-slate-50 text-slate-800 border-slate-200',
    description: 'Pengingat berkala untuk mengunduh snapshot backup database Supabase',
    defaultRoles: ['owner', 'developer'],
    allowPicMode: false,
    icon: 'HardDrive',
  },
};

/**
 * Dispatch Notification to all relevant subscribers
 * @param {Object} options
 * @param {string} options.eventId - ID from NOTIFICATION_EVENTS (e.g. 'invoice_created')
 * @param {string} options.title - Notification title
 * @param {string} options.body - Default body text
 * @param {string} options.badgeText - Badge label (e.g. 'FINANCE')
 * @param {Object} options.actor - User who performed the action { id, full_name, role }
 * @param {Object} [options.targetPic] - Designated PIC user { id, full_name, email }
 * @param {Object} [options.meta] - Additional metadata (e.g. { invoice_id, po_id, amount })
 * @param {string} [options.actionUrl] - Link to navigate when clicked
 */
export async function dispatchNotification({
  eventId,
  title,
  body,
  badgeText,
  actor = {},
  targetPic = null,
  meta = {},
  actionUrl = '',
}) {
  try {
    const eventDef = Object.values(NOTIFICATION_EVENTS).find(e => e.id === eventId) || {
      id: eventId,
      name: title,
      category: badgeText || 'SATALA',
      categoryColor: 'bg-slate-100 text-slate-800 border-slate-200',
      defaultRoles: ['owner', 'developer', 'admin'],
      allowPicMode: true,
    };

    // 1. Fetch all active team members
    const allMembers = await base44.entities.TeamMember.list();
    const activeMembers = allMembers.filter(m => !m.deleted_at && m.status !== 'nonaktif');

    const actorId = actor.id ? String(actor.id) : '';
    const actorName = actor.full_name || actor.name || 'Sistem Satala';
    const picId = targetPic?.id ? String(targetPic.id) : (targetPic?.pic_id ? String(targetPic.pic_id) : '');
    const picName = targetPic?.full_name || targetPic?.name || targetPic?.pic_name || '';

    const notificationsToCreate = [];

    // 2. Loop each team member & check their notification preference
    for (const member of activeMembers) {
      const memberId = String(member.id);

      // ⛔ ATURAN EMAS: ACTOR EXEMPTION
      // Pembuat transaksi tidak pernah menerima notifikasi untuk aksinya sendiri
      if (actorId && memberId === actorId) {
        continue;
      }

      // Check user preferences
      const userSettings = typeof member.notification_preferences === 'string'
        ? JSON.parse(member.notification_preferences || '{}')
        : (member.notification_preferences || {});

      // Mode preference for this specific event: 'pic_only' | 'all' | 'off'
      let eventMode = userSettings[eventId]?.mode;

      // Fallback if not configured explicitly:
      if (!eventMode) {
        const isDefaultRole = eventDef.defaultRoles.includes(member.role?.toLowerCase());
        if (member.role === 'owner' || member.role === 'developer') {
          eventMode = 'all'; // Owner & Dev always monitor all by default
        } else if (isDefaultRole) {
          eventMode = 'all'; // Assigned division receives all by default
        } else {
          eventMode = 'pic_only'; // Other divisions only receive when they are the PIC
        }
      }

      // If user explicitly turned off this event
      if (eventMode === 'off') {
        continue;
      }

      const isUserPic = (picId && memberId === picId) || (picName && member.full_name?.toLowerCase() === picName.toLowerCase());

      // If user is set to 'pic_only', only send if they are the PIC
      if (eventMode === 'pic_only' && !isUserPic) {
        continue;
      }

      // Determine personalized title and body
      let finalTitle = title || eventDef.name;
      let finalBody = body;

      if (isUserPic && actorId && actorId !== memberId) {
        // 🔔 Personalisasi untuk PIC:
        if (eventId === 'invoice_created') {
          finalBody = `Halo ${member.full_name?.split(' ')[0]}! Invoice ${meta.invoice_id || ''} untuk ${meta.brand_name || 'klien'} telah dibuatkan oleh ${actorName} dengan Anda sebagai PIC.`;
        } else if (eventId === 'task_assigned') {
          finalBody = `Anda telah ditugaskan oleh ${actorName} sebagai PIC untuk pengerjaan ${meta.task_name || 'tugas produksi'}.`;
        }
      } else if (!finalBody) {
        finalBody = `Aktivitas baru '${eventDef.name}' telah dicatat oleh ${actorName}.`;
      }

      // Build Notification Record
      notificationsToCreate.push({
        recipient_id: memberId,
        recipient_name: member.full_name,
        recipient_role: member.role,
        event_id: eventId,
        title: finalTitle,
        body: finalBody,
        category: eventDef.category,
        category_color: eventDef.categoryColor,
        actor_id: actorId,
        actor_name: actorName,
        is_pic_notification: isUserPic,
        is_read: false,
        action_url: actionUrl,
        meta: JSON.stringify(meta),
        created_at: new Date().toISOString(),
      });
    }

    // 3. Save In-App Notifications in Batch
    if (notificationsToCreate.length > 0) {
      await Promise.all(
        notificationsToCreate.map(notif => base44.entities.Notification.create(notif).catch(err => {
          console.warn('[NotificationEngine] Gagal menyimpan notifikasi:', err.message);
        }))
      );
    }

    console.log(`[NotificationEngine] Dispatched '${eventId}' to ${notificationsToCreate.length} recipient(s). Actor: ${actorName}`);
    return { success: true, count: notificationsToCreate.length };

  } catch (error) {
    console.error('[NotificationEngine Error]', error);
    return { success: false, error: error.message };
  }
}
